import React, { useState, useEffect } from 'react';
import { Store, Crown, Edit2, Save, X, Plus, Trash2, RotateCcw, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';

const VendorFeaturesManager = () => {
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/vendor-subscriptions/admin/plans');
      setPlans(response.data.plans || {});
    } catch (error) {
      toast.error('Fehler beim Laden der Pläne');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (planId) => {
    setEditingPlan(planId);
    setEditedValues({
      price: plans[planId].price,
      product_limit: plans[planId].product_limit,
      commission_rate: plans[planId].commission_rate,
      features: [...plans[planId].features]
    });
  };

  const cancelEditing = () => {
    setEditingPlan(null);
    setEditedValues({});
    setNewFeature('');
  };

  const savePlan = async () => {
    if (!editingPlan) return;
    
    setSaving(true);
    try {
      await axiosInstance.put(`/vendor-subscriptions/admin/plans/${editingPlan}`, editedValues);
      
      // Update local state
      setPlans(prev => ({
        ...prev,
        [editingPlan]: {
          ...prev[editingPlan],
          ...editedValues
        }
      }));
      
      cancelEditing();
      toast.success('Plan gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    if (!newFeature.trim()) return;
    setEditedValues(prev => ({
      ...prev,
      features: [...prev.features, newFeature.trim()]
    }));
    setNewFeature('');
  };

  const removeFeature = (index) => {
    setEditedValues(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const resetPlans = async () => {
    if (!window.confirm('Alle Pläne auf Standardwerte zurücksetzen?')) return;
    
    try {
      await axiosInstance.post('/vendor-subscriptions/admin/plans/reset');
      toast.success('Pläne zurückgesetzt');
      fetchPlans();
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 px-3 sm:px-4" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Store className="h-6 w-6" />
            Verkäufer-Abos verwalten
          </h1>
          <Button
            variant="outline"
            size="sm"
            onClick={resetPlans}
            className="rounded-none"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Zurücksetzen
          </Button>
        </div>

        <div className="bg-amber-50 border border-amber-200 p-3 rounded-none mb-4 text-sm">
          <p>
            Hier können Sie die Verkäufer-Abonnements konfigurieren: Preise, Produkt-Limits, 
            Provisionen und enthaltene Features für Basis und Gold.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {Object.entries(plans).map(([planId, plan]) => {
            const isEditing = editingPlan === planId;
            const isGold = planId === 'gold';
            
            return (
              <Card 
                key={planId} 
                className={`rounded-none ${isGold ? 'border-2 border-amber-400' : ''}`}
              >
                <CardHeader className={`pb-2 ${isGold ? 'bg-amber-50' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      {isGold ? (
                        <Crown className="h-5 w-5 text-amber-500" />
                      ) : (
                        <Store className="h-5 w-5 text-gray-500" />
                      )}
                      {plan.name}
                    </CardTitle>
                    {!isEditing ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startEditing(planId)}
                        className="rounded-none"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    ) : (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={savePlan}
                          disabled={saving}
                          className="rounded-none text-green-600"
                        >
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={cancelEditing}
                          className="rounded-none text-red-600"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="pt-4 space-y-4">
                  {/* Price */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Monatspreis:</span>
                    {isEditing ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          step="0.01"
                          value={editedValues.price}
                          onChange={(e) => setEditedValues(prev => ({
                            ...prev,
                            price: parseFloat(e.target.value) || 0
                          }))}
                          className="w-20 h-8 text-right rounded-none"
                        />
                        <span className="text-sm">€</span>
                      </div>
                    ) : (
                      <span className="font-bold text-lg">
                        {plan.price === 0 ? 'Kostenlos' : `${formatEUR(plan.price)}`}
                      </span>
                    )}
                  </div>

                  {/* Product Limit */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Produkt-Limit:</span>
                    {isEditing ? (
                      <Input
                        type="number"
                        value={editedValues.product_limit}
                        onChange={(e) => setEditedValues(prev => ({
                          ...prev,
                          product_limit: parseInt(e.target.value) || 0
                        }))}
                        className="w-20 h-8 text-right rounded-none"
                      />
                    ) : (
                      <span className="font-medium">{plan.product_limit}</span>
                    )}
                  </div>

                  {/* Commission Rate */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Provision:</span>
                    {isEditing ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          step="0.1"
                          value={editedValues.commission_rate}
                          onChange={(e) => setEditedValues(prev => ({
                            ...prev,
                            commission_rate: parseFloat(e.target.value) || 0
                          }))}
                          className="w-16 h-8 text-right rounded-none"
                        />
                        <span className="text-sm">%</span>
                      </div>
                    ) : (
                      <span className="font-medium">{plan.commission_rate}%</span>
                    )}
                  </div>

                  {/* Features */}
                  <div className="border-t pt-3">
                    <p className="text-sm font-medium mb-2">Features:</p>
                    <ul className="space-y-1.5">
                      {(isEditing ? editedValues.features : plan.features).map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm">
                          {isEditing ? (
                            <>
                              <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                              <span className="flex-1">{feature}</span>
                              <button
                                onClick={() => removeFeature(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </>
                          ) : (
                            <>
                              <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                              <span>{feature}</span>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                    
                    {isEditing && (
                      <div className="flex gap-2 mt-3">
                        <Input
                          placeholder="Neues Feature..."
                          value={newFeature}
                          onChange={(e) => setNewFeature(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                          className="flex-1 h-8 rounded-none text-sm"
                        />
                        <Button
                          size="sm"
                          onClick={addFeature}
                          className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0"
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-white p-3 rounded-none text-xs text-gray-600">
          <p className="font-medium mb-1">Hinweise:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Änderungen an Preisen gelten nur für neue Abonnements</li>
            <li>Produkt-Limits und Provisionen werden sofort für alle Verkäufer übernommen</li>
            <li>Features werden auf der öffentlichen Preisseite angezeigt</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default VendorFeaturesManager;
